import {
  useGetRecoveryConfigQuery,
  useUpdateRecoveryConfigMutation,
} from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAbility } from 'utilities/ability'

// Value-aware equality for the dirty check. Array fields (e.g.
// approved_reward_ids) are compared order-independently — the editing bodies
// hand back a NEW array on every toggle, so a reference compare (`!==`) would
// read the draft as dirty even after the selection is returned to the saved
// set. The comparison is MULTISET-correct (sort copies + element-wise compare),
// not membership-based: a length + every-includes check would mis-report
// [id1,id1] equal to [id1,id2] (a false "clean" that could drop a real edit).
// Everything else compares strictly. Exported for direct unit testing of the
// multiset semantics.
export const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((item, index) => item === sortedB[index])
  }
  return a === b
}

// Staged/dirty-save model for the Recovery config panes. The two tuning panes
// that still auto-save on select/blur (Recovery generosity, Approved rewards)
// can't be hosted in a modal whose Cancel must roll edits back — a PUT that
// already fired can't be un-committed. This provider holds edits in local
// state and fires the config PUT only on `commit()`, so a modal Cancel maps to
// `rollback()` (drop staged edits, no network) and Save maps to `commit()`.
export interface RecoveryConfigDraftValue {
  // The current view the consumer renders: the seeded config overlaid with any
  // staged edits. `Partial` because the seed is undefined until the GET
  // resolves and only edited keys are guaranteed present.
  draft: Partial<RecoveryConfig>
  // Stage a single field edit. Does NOT hit the network.
  setField: <K extends keyof RecoveryConfig>(
    key: K,
    value: RecoveryConfig[K]
  ) => void
  // CASL manage/MerchantSettings. The three draft-backed bodies (generosity, brand
  // voice, approved rewards) carry no ability check of their own, so this is the
  // only thing standing between a view-only user and an armed Save once the
  // shared modal shell reads it — see ConfigRowSaveAdapter.
  canManage: boolean
  // True when at least one staged value differs from the seeded config.
  isDirty: boolean
  // True while the initial config GET is in flight with no data yet — the
  // consumer should withhold the editing body and show a spinner instead of
  // letting the merchant edit (and Save) defaults that aren't their real config.
  isConfigLoading: boolean
  // True when the config GET failed OR resolved without a payload — the consumer
  // shows a load error rather than the default-valued body, so a broken load
  // can't be mistaken for (or saved over) the merchant's real config.
  isConfigError: boolean
  // True while the commit PUT is in flight (gate Save / show a spinner).
  isSaving: boolean
  // True when the last commit PUT failed (drive an inline error banner).
  isSaveError: boolean
  // Fire ONE PUT with the changed keys (the endpoint tolerates omitted fields),
  // awaiting the result. Resolves `true` on success (staged edits cleared) and
  // `false` on failure (staged edits RETAINED so the merchant can retry or
  // Cancel; isSaveError surfaces). A clean draft is a no-op that resolves true.
  commit: () => Promise<boolean>
  // Drop all staged edits (and any prior save error), restoring `draft` to the
  // seeded config.
  rollback: () => void
}

const RecoveryConfigDraftContext = createContext<
  RecoveryConfigDraftValue | undefined
>(undefined)

// Throws (rather than a silent no-op default) so a `commit`/`setField` outside
// the provider surfaces as a developer error instead of quietly dropping a save.
export const useRecoveryConfigDraft = (): RecoveryConfigDraftValue => {
  const value = useContext(RecoveryConfigDraftContext)
  if (value === undefined) {
    throw new Error(
      'useRecoveryConfigDraft must be used within a RecoveryConfigDraftProvider'
    )
  }
  return value
}

type Props = {
  children: React.ReactNode
  /**
   * Fired with the committed fields after the PUT succeeds — never on failure or
   * on a clean no-op commit. Lets a host mirror the saved values into its own
   * state; the wizard uses it so its Finalize, which re-sends these fields from
   * once-hydrated state, can't clobber an edit made here. Does not affect the PUT.
   */
  onCommitted?: (staged: Partial<RecoveryConfig>) => void
}

export const RecoveryConfigDraftProvider = ({
  children,
  onCommitted,
}: Props) => {
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  const {
    data: seed,
    isLoading: isConfigLoading,
    isError: isConfigFetchError,
  } = useGetRecoveryConfigQuery()
  const [updateConfig, { isLoading: isSaving, isError: isSaveError, reset }] =
    useUpdateRecoveryConfigMutation()
  const [staged, setStaged] = useState<Partial<RecoveryConfig>>({})

  // No usable config once the GET settles: an outright error OR a resolved-but-
  // empty payload (seed == null). Distinct from isConfigLoading so the consumer
  // shows a spinner while loading and an error only after it settles badly —
  // never the default-valued body, which a Save could persist over real config.
  const isConfigError = !isConfigLoading && (isConfigFetchError || seed == null)

  // commit() awaits the PUT, then clears staged. If the provider unmounts during
  // that await (e.g. the operator navigates away mid-save), the post-await
  // setStaged would run on an unmounted component — guard it. Mirrors the panes'
  // hydrate-once ref discipline.
  const isMountedRef = useRef(true)
  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    []
  )

  const setField = useCallback(
    <K extends keyof RecoveryConfig>(key: K, value: RecoveryConfig[K]) => {
      setStaged(prev => ({ ...prev, [key]: value }))
    },
    []
  )

  // Dirty only when a staged value actually differs from the seed (value-aware,
  // so an array toggled back to the saved set is clean) — Save stays disabled
  // and `commit` won't fire a pointless PUT. Read-only comparison; assigning
  // back through a union-typed key would be unsound, so `commit` sends the
  // staged partial as-is rather than rebuilding it.
  const isDirty = useMemo(
    () =>
      (Object.keys(staged) as (keyof RecoveryConfig)[]).some(
        key => !valuesEqual(staged[key], seed?.[key])
      ),
    [staged, seed]
  )

  const draft = useMemo(() => ({ ...seed, ...staged }), [seed, staged])

  // Fire the PUT and AWAIT it: clear staged only on success so a failed save
  // keeps the merchant's edits (for retry / Cancel) and surfaces via
  // isSaveError. Returns success so the caller gates close/navigation. Mirrors
  // the panes' useSaveAndTest (which also swallows the rejection — isSaveError
  // drives the banner — rather than rethrowing).
  const commit = useCallback(async (): Promise<boolean> => {
    // Clear any stale mutation state (incl. a prior isSaveError) up front, so
    // even the no-op clean path returns with a clean banner — the contract is
    // "a true return means mutation state is clean".
    reset()
    // Belt-and-suspenders: never PUT a config derived from an unresolved seed.
    // The editing bodies are already gated on isConfigLoading/isConfigError so a
    // staged edit can't exist before the GET lands, but guard the write too.
    if (seed == null) return false
    if (!isDirty) {
      setStaged({})
      return true
    }
    try {
      // `staged` holds only the keys the merchant touched; the PUT tolerates
      // omitted fields, so this sends just those keys, never the whole config.
      await updateConfig(staged).unwrap()
      // Announce the committed keys BEFORE clearing them, and independently of
      // mount state: the host being notified (e.g. the wizard's own context) can
      // outlive this provider, and a save that landed must be mirrored even if the
      // modal has since closed.
      onCommitted?.(staged)
      // Only clear if still mounted — the await may have outlived the provider.
      if (isMountedRef.current) setStaged({})
      return true
    } catch {
      return false
    }
  }, [seed, isDirty, staged, updateConfig, reset, onCommitted])

  const rollback = useCallback(() => {
    reset()
    setStaged({})
  }, [reset])

  const value = useMemo(
    () => ({
      draft,
      setField,
      canManage,
      isDirty,
      isConfigLoading,
      isConfigError,
      isSaving,
      isSaveError,
      commit,
      rollback,
    }),
    [
      draft,
      setField,
      canManage,
      isDirty,
      isConfigLoading,
      isConfigError,
      isSaving,
      isSaveError,
      commit,
      rollback,
    ]
  )

  return (
    <RecoveryConfigDraftContext.Provider value={value}>
      {children}
    </RecoveryConfigDraftContext.Provider>
  )
}

export default RecoveryConfigDraftContext
